import { formatMoney } from '@theme/theme-currency';
import { ThemeEvents } from '@theme/events';

class CartShippingEstimator extends HTMLElement {
  constructor() {
    super();
    this.pollCount = 0;
    this.maxAttempts = 5;
    this.pollInterval = 500;
    this.refresh = this.refresh.bind(this);
  }

  connectedCallback() {
    this.zipInput = this.querySelector('#cart-zip');
    this.countrySelect = this.querySelector('#AddressCountry');
    this.provinceSelect = this.querySelector('#AddressProvince');
    this.provinceContainer = this.querySelector('#AddressProvinceContainer');
    this.countryWrapper = this.querySelector('#CountrySelectWrapper');
    this.provinceWrapper = this.querySelector('#ProvinceSelectWrapper');
    this.calcButton = this.querySelector('[data-action="calculate"]');
    this.formWrapper = this.querySelector('.b-zip_form');

    this.output = document.querySelector('[data-shipping-output]');
    this.infoWrapper = document.querySelector('.js-shipping-info-wrapper');
    this.zipDisplay = document.querySelector('.current-zip-code');
    this.predictionContainer = document.querySelector('.b-delivery-prediction');
    this.editButton = document.querySelector('.js-edit-shipping');
    this.footer = document.querySelector('#main-cart-footer');
    this.totalDisplay = document.querySelector('[ref="cartTotal"]');
    this.zipHint = document.querySelector('.js-zip-hint');

    this.currencySettings = {
      currencyCodeEnabled: window.OtterVars?.MarketSettings?.currencyCodeEnabled,
      moneyWithCurrencyFormat: window.OtterVars?.MarketSettings?.moneyWithCurrencyFormat
    };

    const cache = JSON.parse(sessionStorage.getItem('shipping_cache'));

    if (cache && (Date.now() - cache.ts < 300000)) {
      this.initRegionDisplay(cache.zip, cache.state);
      this.hideForm();
      if (this.zipDisplay) this.zipDisplay.textContent = cache.zip;
      this.render(cache.rates, cache.zip);
    } else {
      this.initRegionDisplay();
      this.showForm();
    }

    this.countrySelect?.addEventListener('change', () => this.updateProvinces());
    this.calcButton?.addEventListener('click', () => this.calculate());
    this.editButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.showForm();
    });

    document.addEventListener(ThemeEvents.cartUpdate, this.refresh);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.refresh);
  }

  initRegionDisplay(cachedZip, cachedState) {
    const storeRegion = (window.OtterVars?.MarketSettings?.storeRegion || 'NA').toLowerCase();
    const currentCountry = window.OtterVars?.MarketSettings?.countryName || 'United States';

    this.setCountryByMarket(currentCountry);

    if (storeRegion === 'ireland' || storeRegion === 'emea') {
      if (this.countryWrapper) this.countryWrapper.style.display = 'block';
    } else {
      if (this.countryWrapper) this.countryWrapper.style.display = 'none';
    }

    if (this.provinceContainer) this.provinceContainer.style.display = 'block';

    this.updateProvinces();

    if (cachedZip) this.zipInput.value = cachedZip;
    if (cachedState) this.provinceSelect.value = cachedState;
  }

  showForm() {
    if (this.formWrapper) this.formWrapper.style.display = 'flex';
    if (this.infoWrapper) this.infoWrapper.style.display = 'none';
    if (this.zipHint) this.zipHint.style.display = 'block';
  }

  hideForm() {
    if (this.formWrapper) this.formWrapper.style.display = 'none';
    if (this.infoWrapper) this.infoWrapper.style.display = 'block';
    if (this.zipHint) this.zipHint.style.display = 'none';
  }

  async calculate(isCached = false) {
    let zip = this.zipInput?.value.trim();
    let state = this.provinceSelect?.value || '';
    let country = window.OtterVars?.MarketSettings?.countryName || 'United States';

    if (this.countrySelect?.value !== '---') {
      country = this.countrySelect?.value;
    }

    if (isCached) {
      const cachedData = JSON.parse(sessionStorage.getItem('shipping_cache'));
      if (cachedData) {
        zip = cachedData.zip;
        state = cachedData.state;
      }
    }

    if (!zip) return;

    if (this.provinceWrapper && this.provinceWrapper.style.display !== 'none' && !state) {
      alert(Theme.translations.error_state);
      return;
    }

    this.toggleLoading(true);
    this.pollCount = 0;

    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams();

    params.append('shipping_address[zip]', zip);
    params.append('shipping_address[country]', country);
    params.append('shipping_address[province]', state);

    try {
      const response = await fetch(`${root}cart/prepare_shipping_rates.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      });

      if (response.ok || response.status === 202) {
        this.poll(zip, state);
      } else {
        this.toggleLoading(false);
      }
    } catch (e) {
      this.toggleLoading(false);
    }
  }

  async poll(zip, state) {
    const hasCache = !!sessionStorage.getItem('shipping_cache');

    if (this.pollCount >= this.maxAttempts) {
      this.toggleLoading(false);

      if (!hasCache && this.output) {
        this.output.textContent = Theme.translations.calc_fallback;
      }

      return;
    }

    const root = window.Shopify?.routes?.root || '/';

    try {
      const res = await fetch(`${root}cart/async_shipping_rates.json`);

      if (res.status === 202) {
        this.pollCount++;
        setTimeout(() => this.poll(zip, state), this.pollInterval);
      } else if (res.status === 200) {
        const data = await res.json();
        this.success(data.shipping_rates, zip, state);
      }
    } catch (e) {
      this.toggleLoading(false);

      if (!hasCache && this.output) {
        this.output.textContent = Theme.translations.calc_fallback;
      }
    }
  }

  success(rates, zip, state) {
    if (!rates || rates.length === 0) {
      if (this.output) this.output.textContent = Theme.translations.no_rates;
      this.showForm();
      this.toggleLoading(false);
      return;
    }
    sessionStorage.setItem('shipping_cache', JSON.stringify({ zip, state, rates, ts: Date.now() }));

    if (this.zipDisplay) this.zipDisplay.textContent = zip;
    this.hideForm();
    this.render(rates, zip);
  }

  render(rates, zip) {
    const rate = rates[0];
    const shippingPrice = parseFloat(rate.price);

    if (this.output) {
      this.output.textContent = shippingPrice === 0 ? "FREE" : formatMoney(shippingPrice, this.currencySettings);
    }

    this.updateCartTotal(shippingPrice);

    const preOrder = !!document.querySelector('.m-warning');
    if (!preOrder) {
      this.getWonderment(zip, rate.name);
    } else if (this.predictionContainer) {
      this.predictionContainer.textContent = Theme.translations.preorder_note;
      this.toggleLoading(false);
    }
  }

  async updateCartTotal(shippingAmount) {
    if (!this.totalDisplay) return;
    try {
      const res = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cart = await res.json();
      const newTotalCents = cart.total_price + (shippingAmount * 100);
      const formattedTotal = formatMoney(newTotalCents / 100, this.currencySettings);
      this.totalDisplay.textContent = formattedTotal;
    } catch (e) {}
  }

  async getWonderment(zip, rateName) {
    const storefront = window.OtterVars?.MarketSettings?.storeRegion || 'NA';
    const langCode = window.OtterVars?.MarketSettings?.languageCode || 'en';
    const url = `https://api.otterproducts.com/ecommerce-exp/api/shopify/delivery-predictions?zip=${zip}&storefront=${storefront}`;

    try {
      const res = await fetch(url, { headers: { 'x-api-key': this.dataset.apiKey } });
      if (res.ok) {
        const data = await res.json();
        const predictions = data.predictions || [];
        const match = predictions.find(p =>
          p.shippingMethod.toLowerCase().includes(rateName.toLowerCase()) ||
          rateName.toLowerCase().includes(p.shippingMethod.toLowerCase())
        );

        if (this.predictionContainer) {
          if (match && match.estimatedDeliveryDate) {
            const date = new Date(match.estimatedDeliveryDate);
            const formattedDate = date.toLocaleDateString(langCode, { month: 'short', day: 'numeric' });
            this.predictionContainer.innerHTML = `. ${Theme.translations.get_it_by} <strong>${formattedDate}</strong>. `;
          } else {
            this.predictionContainer.innerHTML = '. ';
          }
        }

        this.toggleLoading(false);
      }
    } catch (e) {
      this.toggleLoading(false);
    }
  }

  toggleLoading(state) {
    const footer = document.querySelector('.cart__summary-inner');

    setTimeout(() => {
      footer?.classList.toggle('m-is_loading', state);
    }, 0);

    const btnText = this.calcButton?.querySelector('.button-text');
    const spinner = this.calcButton?.querySelector('.spinner');
    if (btnText) btnText.style.display = state ? 'none' : 'block';
    if (spinner) spinner.style.display = state ? 'block' : 'none';
    if (this.calcButton) this.calcButton.disabled = state;
  }

  setCountryByMarket(currentCountry) {
    if (!this.countrySelect) return;
    const countryToSet = currentCountry || window.OtterVars?.MarketSettings?.countryName || 'United States';
    Array.from(this.countrySelect.options).forEach(option => {
      if (option.text.toLowerCase() === countryToSet.toLowerCase()) {
        option.selected = true;
      }
    });
  }

  updateProvinces() {
    if (!this.countrySelect || !this.provinceSelect) return;
    const selectedOption = this.countrySelect.options[this.countrySelect.selectedIndex];
    const provinces = JSON.parse(selectedOption.getAttribute('data-provinces') || '[]');
    this.provinceSelect.innerHTML = '';
    if (provinces.length > 0) {
      if (this.provinceWrapper) this.provinceWrapper.style.display = 'block';
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = Theme.translations.select_region;
      this.provinceSelect.appendChild(placeholder);
      provinces.forEach(p => {
        const opt = document.createElement('option');
        opt.value = p[0];
        opt.textContent = p[1];
        this.provinceSelect.appendChild(opt);
      });
    } else {
      if (this.provinceWrapper) this.provinceWrapper.style.display = 'none';
      this.provinceSelect.value = '';
    }
  }

  refresh() {
    const preOrder = !!document.querySelector('.m-warning');
    const cachedData = JSON.parse(sessionStorage.getItem('shipping_cache'));

    if (preOrder) {
      if (this.predictionContainer) this.predictionContainer.textContent = Theme.translations.preorder_note;
      sessionStorage.removeItem('shipping_cache');
      this.showForm();
    } else if (cachedData && cachedData.zip) {
      this.hideForm();
      if (this.zipInput) this.zipInput.value = cachedData.zip;
      if (this.zipDisplay) this.zipDisplay.textContent = cachedData.zip;

      this.calculate(true);
    }
  }
}

if (!customElements.get('cart-shipping-estimator')) {
  customElements.define('cart-shipping-estimator', CartShippingEstimator);
}